// Formatacao e parsing de valores monetarios em BRL.
//
// O backend retorna `amount` como string decimal (ex: "350.75") em listings
// e como `number` em sumarios. Sempre que falamos com a API em mutations
// usamos o numeric. Para entrada do usuario via `<CurrencyInput/>`, a string
// vem em formato BR (ex: "350,75") e precisa ser convertida para o formato
// canonico ponto-decimal antes de enviar.
//
// Espelha utilidades do mobile (`grana-money-app/src/shared/lib/currency.ts`).

const FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formata um numero ou string decimal em BRL (ex: 350.75 -> "R$ 350,75").
 * Aceita string canonica (ponto decimal) ou number. Strings invalidas
 * retornam "R$ 0,00" — defensivo para evitar quebrar UI com NaN.
 */
export function formatBRL(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return FORMATTER.format(0);
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return FORMATTER.format(0);
  return FORMATTER.format(num);
}

/**
 * Converte string em formato BR (com virgula como decimal e ponto como
 * separador de milhar) para numero canonico. Ex: "1.350,75" -> 1350.75.
 * Aceita tambem strings ja em formato canonico ("1350.75").
 */
export function parseBRLToNumber(input: string): number {
  if (!input) return 0;
  // Remove tudo que nao e digito, virgula, ponto ou hifen.
  const sanitized = input.replace(/[^\d,.-]/g, '');
  // Se contem virgula, e formato BR — remover pontos (milhar) e trocar virgula por ponto.
  if (sanitized.includes(',')) {
    const normalized = sanitized.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(normalized);
    return Number.isFinite(num) ? num : 0;
  }
  const num = parseFloat(sanitized);
  return Number.isFinite(num) ? num : 0;
}

/**
 * Converte para string canonica decimal com 2 casas (ex: "350.75").
 * Usado ao montar payloads para a API que espera `amount` numerico mas
 * tolera string com 2 casas.
 */
export function toDecimalString(input: string | number): string {
  const num = typeof input === 'string' ? parseBRLToNumber(input) : input;
  if (!Number.isFinite(num)) return '0.00';
  return num.toFixed(2);
}

/**
 * Gera label compacto de delta (% de variacao) — exibido em balance card.
 * Ex: 0.123 -> "+12,3%", -0.05 -> "-5,0%".
 */
export function formatPercentDelta(ratio: number): string {
  if (!Number.isFinite(ratio)) return '0,0%';
  const pct = ratio * 100;
  const sign = pct > 0 ? '+' : pct < 0 ? '' : '';
  return `${sign}${pct.toFixed(1).replace('.', ',')}%`;
}
